import type { Environment, PlanktonState } from './types';
import { STANDARD_DEPTHS_M } from './data/water/woa23';
import type { MonthBlend, WaterClimatology } from './waterColumn';
import { held } from './held';

/**
 * The plankton in a place's mixed layer (M52): phosphate, mmol P m⁻³, and
 * nitrate, phytoplankton, zooplankton, detritus and nitrogen fixers, mmol N
 * m⁻³, mixed evenly from the surface to the mixed layer's floor. A
 * deterministic step that draws nothing, once a day after the environment
 * (`GDD.md` section 9: "growth toward a ceiling set by light, nutrients and
 * temperature").
 *
 * **The biology** is the NPZD of the University of Victoria Earth System
 * Climate Model (UVic ESCM 2.8; Schmittner, A., Oschlies, A., Giraud, X.,
 * Eby, M. and Simmons, H. L. 2005, Global Biogeochemical Cycles 19: GB3004,
 * after Oschlies and Garçon 1999 and the optimized set of Schartau and
 * Oschlies 2003): its equations and its values as the model ran them for
 * Schmittner et al. 2013 (Biogeosciences 10: 5793), **as read** in its code
 * and run controls archived at NOAA NCEI (accession 0157698: `npzd_src.F`,
 * `setocn.F`'s defaults, `control.in`'s settings; `scripts/data/uvic.ts`
 * archives them and `tests/plankton.test.ts` holds these rates to them):
 *
 * - growth, the smallest of light's, phosphate's and nitrate's limits
 *   (Liebig's law) times Eppley's temperature factor b^(cT): light's after
 *   Evans and Parslow 1985, the day's growth averaged over the layer and a
 *   triangular day; the nutrients' saturating;
 * - nitrogen fixers (diazotrophs), slower growing, limited by light and
 *   phosphate and not by nitrate: what they take of nitrate is as much of
 *   their growth as nitrate's saturation gives, the rest fixed from the air,
 *   new nitrogen; grazed as the phytoplankton are, dying linearly to
 *   detritus. Without them a mixed layer whose water below holds no nitrate
 *   within its reach starves: Maui Nui's, the Atlas's nitrate none in some
 *   month at every depth to 100 m (at most 0.62 µmol kg⁻¹) and the layer 28
 *   to 57 m deep (MEASURED, M52: without the fixers, at its fitted mixing
 *   then, 0.1995 m d⁻¹, its phytoplankton fell from 0.099 to 0.006 mmol N
 *   m⁻³ in a year, its zooplankton to none; review M62, from today's table
 *   at today's mixing, 0.5012 m d⁻¹, from 0.093 to 0.0002);
 * - grazing, Holling's type III: g = gmax ε P² / (gmax + ε P²), times the
 *   temperature factor, a share assimilated and the rest to detritus;
 * - phytoplankton mortality, quadratic to detritus and linear (with the
 *   temperature factor) back to nitrate; zooplankton mortality, quadratic,
 *   and excretion to nitrate; detritus remineralized to nitrate, and sinking.
 *
 * Where it departs from the model's code, as a mixed layer must (Evans and
 * Parslow's slab):
 *
 * - **No iron.** UVic scales growth by an iron-limitation map its authors
 *   made, whose terms give a citation and no leave for a game that may be
 *   sold (M31): the game's growth is the map's unlimited (open, M52; the
 *   Southern Ocean and the subarctic Pacific, where iron limits growth,
 *   will grow too much).
 * - **The light's layer mean** takes one attenuation, water's and the
 *   phytoplankton's together, in the factor as in the exponential (UVic's
 *   code takes water's alone in the factor, an approximation in its thin
 *   layers that a mixed layer tens of meters deep does not allow).
 * - **The mixed layer's physics.** As it deepens, water from below is mixed
 *   in: the Atlas's nutrients over the layer taken in (its µmol kg⁻¹ by the
 *   water's density, EOS-80), and no plankton or detritus. As it shoals,
 *   what lies below is left behind. Across its floor water mixes at so many
 *   meters a day, each place its own (`PlanktonPlaceTable`, MEASURED by fit
 *   to its surface's nitrate and phosphate where the satellite sees the
 *   plankton live: the Atlas's, M56; Maui Nui's HOT's at Kahe Point, M60;
 *   the five low bands' the Atlas's corrected by nanomolar samples, M61; the form of Fasham, Ducklow and McKelvie 1990's
 *   slab, J. Mar. Res. 48, whose m stands for diffusion alone, 0.1 m d⁻¹ at
 *   Bermuda, "approximately equivalent to a vertical diffusion coefficient
 *   of 0.3 cm² s⁻¹ across a 20 m thermocline"), bringing the Atlas's nutrients up and taking
 *   plankton and detritus down: it stands for every way water reaches the
 *   layer from below that a slab does not have (upwelling, eddies, the
 *   nitracline's diffusion) and absorbs what the biology misses (iron), as
 *   the coral's other losses do (M47). Detritus sinks out at the model's
 *   speed at the floor's depth.
 * - **The sunlight** is the game's own (`surfaceParMol`, NASA's satellite),
 *   turned from photons to watts as the model counts it (`MICROMOL_PER_J`).
 */

export interface PlanktonRates {
  /** Growth: a, d⁻¹, at 0 °C, the phytoplankton's and the nitrogen fixers'; Eppley's b and c, °C⁻¹; the P-I curve's initial slope, (W m⁻²)⁻¹ d⁻¹; nitrate's half saturation, mmol N m⁻³ (phosphate's, its Redfield share). */
  maxGrowthPerDay: number;
  fixersMaxGrowthPerDay: number;
  eppleyBase: number;
  eppleyPerC: number;
  initialSlope: number;
  nitrateHalfSat: number;
  /** Light's attenuation by water, m⁻¹, and by phytoplankton, m⁻¹ (mmol N m⁻³)⁻¹. */
  waterAttenuation: number;
  selfShading: number;
  /** Phytoplankton mortality: quadratic, (mmol N m⁻³)⁻¹ d⁻¹, and linear, d⁻¹ at 0 °C. */
  phytoQuadratic: number;
  phytoLinear: number;
  /** Grazing: the most, d⁻¹ at 0 °C; prey capture, (mmol N m⁻³)⁻² d⁻¹; the share assimilated. */
  maxGrazing: number;
  preyCapture: number;
  assimilated: number;
  /** Zooplankton: quadratic mortality, (mmol N m⁻³)⁻¹ d⁻¹, and excretion, d⁻¹ at 0 °C. */
  zooQuadratic: number;
  excretion: number;
  /** Detritus: remineralization, d⁻¹ at 0 °C; sinking at the surface, m d⁻¹, and its gain with depth, d⁻¹. */
  remineralization: number;
  sinkingMPerDay: number;
  sinkingGainPerDay: number;
  /** Phosphorus to nitrogen in organic matter, mol mol⁻¹ (Redfield's). */
  phosphorusPerNitrogen: number;
}

/**
 * The game's rates (above), field figures: UVic's as the run of Schmittner
 * et al. 2013 set them, read in its code (NCEI's archive, byte for byte the
 * paper's own supplement). The paper prints six (p. 5795: a_PO 0.5, a_PD
 * 0.08, g 0.6, ε 2, k_c 0.047, µ_P0 0.01) and takes the rest from Schmittner
 * et al. 2008's Table A1 as amended by Schmittner et al. 2009b, unread here
 * (the publisher's bot check, review M62): the other 13 are the code's
 * alone. The paper gives the detritus's sinking as w_D = 7; the code it
 * published runs `wd0 = 6.0` and its namelist sets no other, and the game
 * keeps the run as it ran. The mixing is each place's, `PlanktonPlaceTable`.
 */
export const PLANKTON_RATES: Readonly<PlanktonRates> = Object.freeze({
  maxGrowthPerDay: 0.5,
  fixersMaxGrowthPerDay: 0.08,
  eppleyBase: 1.066,
  eppleyPerC: 1,
  initialSlope: 0.1,
  nitrateHalfSat: 0.7,
  waterAttenuation: 0.04,
  selfShading: 0.047,
  phytoQuadratic: 0.025,
  phytoLinear: 0.01,
  maxGrazing: 0.6,
  preyCapture: 2,
  assimilated: 0.925,
  zooQuadratic: 0.34,
  excretion: 0.01,
  remineralization: 0.048,
  sinkingMPerDay: 6,
  sinkingGainPerDay: 0.04,
  phosphorusPerNitrogen: 1 / 16,
});

/**
 * A place's plankton as built (`scripts/plankton.ts`, into
 * `data/plankton/places.ts`): the mixing across its mixed layer's floor, m
 * d⁻¹, MEASURED as the rate that brings its nitrate and phosphate nearest
 * its surface's through the mean year (the Atlas's, M56; Maui Nui's HOT's at
 * Kahe Point, M60; the five low bands' the Atlas's corrected, M61) where its
 * plankton lives in every month the satellite sees chlorophyll; and its plankton at each month's start in that year,
 * spun up until a year repeats itself, which a new world opens from.
 */
export interface PlanktonPlaceTable {
  crossMixingMPerDay: number;
  monthStart: readonly PlanktonState[];
}

/**
 * Photons in a joule of sunlight's PAR, µmol J⁻¹: COMPUTED for a spectrum
 * flat in energy from 400 to 700 nm, so the band's mean wavelength 550 nm,
 * over Planck's constant times light's speed (both exact in the SI) and
 * Avogadro's number (exact): 4.598. (The figure the field uses for
 * sunlight, about 4.6, rests on the same reasoning.)
 */
export const MICROMOL_PER_J = (550e-9 / (6.62607015e-34 * 299792458) / 6.02214076e23) * 1e6;

/** Seconds in a day, and the biology's steps a day: UVic's run stepped its tracers every 1.25 days (`control.in`'s dtts, 108,000 s) and its biology four times in each (dtnpzd, 27,000 s); the game's step is a day, so four of 6 hours (a choice). */
const DAY_S = 86400;
const STEPS_A_DAY = 4;
/** The least growth ratio UVic lets the light's function take (its 1e-6). */
const LEAST_RATIO = 1e-6;

/** The day's sunlight at the surface as a mean over its 24 hours, PAR, W m⁻²: its photons turned to watts as the model counts them. */
export const dayMeanParW = (env: Environment): number => (env.surfaceParMol * 1e6) / DAY_S / MICROMOL_PER_J;

/** Evans and Parslow's light function: the integral of a Smith curve over a triangular day, in the ratio of the noon light's growth to the most. */
function lightIntegral(u: number): number {
  const r = Math.sqrt(1 + u * u);
  return Math.log(u + r) - (r - 1) / u;
}

/** The Atlas's nutrients on the day, µmol kg⁻¹ (its water and the day's two months), and the water's density, kg m⁻³, which turns them to the model's mmol m⁻³. */
export interface Nutrients {
  water: WaterClimatology;
  blend: MonthBlend;
  densityKgM3: number;
}

/**
 * A tracer at a depth on the day, µmol kg⁻¹: the day's profile
 * (`profileOn`'s, to the bit) between the standard depths, held at its
 * deepest below them, read where it is wanted rather than built whole (M52:
 * the plankton reads it at a depth or two a day in every place).
 */
function atDepth(n: Nutrients, tracer: 'nitrate' | 'phosphate', depthM: number): number {
  const { from, to, toward } = n.blend;
  const a = n.water.monthly[tracer][from] as readonly number[];
  const b = n.water.monthly[tracer][to] as readonly number[];
  const deep = n.water.deep[tracer];
  const levels = a.length + deep.length;
  for (let k = 1; k < levels; k++) {
    const d = STANDARD_DEPTHS_M[k] as number;
    if (depthM <= d) {
      const d0 = STANDARD_DEPTHS_M[k - 1] as number;
      const v0 = levelOn(a, b, deep, toward, k - 1);
      return v0 + (levelOn(a, b, deep, toward, k) - v0) * ((depthM - d0) / (d - d0));
    }
  }
  return levelOn(a, b, deep, toward, levels - 1);
}

/** A standard level's value on the day: its two months' blend, or the annual mean below them (`profileOn`). */
function levelOn(a: readonly number[], b: readonly number[], deep: readonly number[], toward: number, k: number): number {
  if (k >= a.length) return deep[k - a.length] as number;
  const v = a[k] as number;
  return v + ((b[k] as number) - v) * toward;
}

/** What the zooplankton graze of a prey a day, mmol N m⁻³ d⁻¹: Holling's third form, at most `maxGrazing` a day, faster as the water warms (Eppley's). */
function grazedOf(prey: number, zoo: number, eppley: number, rates: PlanktonRates): number {
  const caught = rates.preyCapture * prey * prey;
  return ((rates.maxGrazing * caught) / (rates.maxGrazing + caught)) * zoo * eppley;
}

/** A tracer's mean between two depths: the day's profile's mean by the trapezoid at a meter's step (a definition, finer than its levels). */
function meanBetween(n: Nutrients, tracer: 'nitrate' | 'phosphate', fromM: number, toM: number): number {
  const steps = Math.max(1, Math.ceil(toM - fromM));
  let sum = 0;
  for (let i = 0; i <= steps; i++) sum += atDepth(n, tracer, fromM + ((toM - fromM) * i) / steps) * (i === 0 || i === steps ? 0.5 : 1);
  return sum / steps;
}

/**
 * The day's growth's ceiling set by light, d⁻¹: Evans and Parslow's layer
 * mean (above), at a most `jmax`, d⁻¹, in a layer `mixedM` deep whose light
 * fades at `k` per meter, `fade` its share left at the floor.
 */
export function lightLimited(env: Environment, jmax: number, k: number, fade: number, mixedM: number, rates: PlanktonRates = PLANKTON_RATES): number {
  const dayShare = env.dayLengthHours / 24;
  if (!(dayShare > 0) || !(env.surfaceParMol > 0) || !(jmax > 0)) return 0;
  // The day's mean PAR in W m⁻², and its noon on a triangular day, as the model's light.
  const meanW = dayMeanParW(env);
  const gd = jmax * dayShare;
  const u1 = Math.max((2 * rates.initialSlope * meanW) / gd, LEAST_RATIO);
  return (gd / (k * mixedM)) * (lightIntegral(u1) - lightIntegral(u1 * fade));
}

/**
 * The plankton a day on, in the day's environment and the Atlas's nutrients
 * for the day: first the mixed layer's change (water from below taken in as
 * it deepens, what lies below left as it shoals), then the day in four steps
 * of biology and mixing across the floor.
 */
export function nextPlankton(p: PlanktonState, env: Environment, nutrients: Nutrients, crossMixingMPerDay: number, rates: PlanktonRates = PLANKTON_RATES): PlanktonState {
  const h = env.mixedLayerM;
  // µmol kg⁻¹ times kg m⁻³ is µmol m⁻³, a thousand to the model's mmol m⁻³ (a definition).
  const perKg = nutrients.densityKgM3 / 1000;
  let { phosphate, nitrate, phyto, zoo, detritus, fixers } = p;
  if (h > p.mixedLayerM) {
    const kept = p.mixedLayerM / h;
    phosphate = phosphate * kept + meanBetween(nutrients, 'phosphate', p.mixedLayerM, h) * perKg * (1 - kept);
    nitrate = nitrate * kept + meanBetween(nutrients, 'nitrate', p.mixedLayerM, h) * perKg * (1 - kept);
    phyto *= kept;
    zoo *= kept;
    detritus *= kept;
    fixers *= kept;
  }
  const phosphateBelow = atDepth(nutrients, 'phosphate', h) * perKg;
  const nitrateBelow = atDepth(nutrients, 'nitrate', h) * perKg;
  const eppley = rates.eppleyBase ** (rates.eppleyPerC * env.seaSurfaceC);
  const jmax = rates.maxGrowthPerDay * eppley;
  const jmaxFixers = rates.fixersMaxGrowthPerDay * eppley;
  const phosphateHalfSat = rates.nitrateHalfSat * rates.phosphorusPerNitrogen;
  const mix = crossMixingMPerDay / h;
  const sink = (rates.sinkingMPerDay + rates.sinkingGainPerDay * h) / h;
  // The light's limits, once a day from the day's first phytoplankton, as UVic takes them once a
  // step before its biology's steps (`npzd_src.F`): through the layer, shaded by the phytoplankton
  // (not by the fixers). Within a layer UVic fades the light by water's attenuation as it is, its
  // noon sun's slant taken only on the way to deeper layers; a mixed layer begins at the surface.
  const k = rates.waterAttenuation + rates.selfShading * phyto;
  const fade = Math.exp(-k * h);
  const light = lightLimited(env, jmax, k, fade, h, rates);
  const fixersLight = lightLimited(env, jmaxFixers, k, fade, h, rates);
  const dt = 1 / STEPS_A_DAY;
  for (let s = 0; s < STEPS_A_DAY; s++) {
    const phosphateLimit = phosphate / (phosphateHalfSat + phosphate);
    const nitrateLimit = nitrate / (rates.nitrateHalfSat + nitrate);
    const growth = Math.min(light, jmax * phosphateLimit, jmax * nitrateLimit);
    const fixerGrowth = Math.min(fixersLight, jmaxFixers * phosphateLimit);
    const npp = growth * phyto;
    const fixersNpp = fixerGrowth * fixers;
    const fixersNitrate = nitrateLimit * fixersNpp;
    const grazing = grazedOf(phyto, zoo, eppley, rates);
    const fixersGrazing = grazedOf(fixers, zoo, eppley, rates);
    const phytoQuad = rates.phytoQuadratic * phyto * phyto;
    const phytoLinear = rates.phytoLinear * eppley * phyto;
    const fixersDying = rates.phytoLinear * eppley * fixers;
    const zooQuad = rates.zooQuadratic * zoo * zoo;
    const excretion = rates.excretion * eppley * zoo;
    const remineralized = rates.remineralization * eppley * detritus;
    const regenerated = remineralized + excretion + phytoLinear;
    phosphate += dt * (rates.phosphorusPerNitrogen * (regenerated - npp - fixersNpp) + mix * (phosphateBelow - phosphate));
    nitrate += dt * (regenerated - npp - fixersNitrate + mix * (nitrateBelow - nitrate));
    phyto += dt * (npp - phytoQuad - grazing - phytoLinear - mix * phyto);
    zoo += dt * (rates.assimilated * (grazing + fixersGrazing) - excretion - zooQuad - mix * zoo);
    detritus += dt * ((1 - rates.assimilated) * (grazing + fixersGrazing) + phytoQuad + fixersDying + zooQuad - remineralized - (sink + mix) * detritus);
    fixers += dt * (fixersNpp - fixersDying - fixersGrazing - mix * fixers);
    // Never below none (a step cannot take more than there is: a guard, as UVic's flags are).
    phosphate = Math.max(0, phosphate);
    nitrate = Math.max(0, nitrate);
    phyto = Math.max(0, phyto);
    zoo = Math.max(0, zoo);
    detritus = Math.max(0, detritus);
    fixers = Math.max(0, fixers);
  }
  return { phosphate: held(phosphate), nitrate: held(nitrate), phyto: held(phyto), zoo: held(zoo), detritus: held(detritus), fixers: held(fixers), mixedLayerM: held(h) };
}
